import { Fragment, Suspense } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';

import MainContentPending from '../../../pageStructure/mainContentPending';
import RepayEarlyForFree from '../../OutstandingView/dashboardActions/RepayEarlyForFree/RepayEarlyForFree';
import FutureRepayments from '../futureRepayment/FutureRepayments';
import { OverduePayments } from '../overduePayments/OverduePayments';
import PastRepayments from '../pastRepayments/PastRepayments';
import { useGetRepaymentsPanelData } from '../RepaymentsPanel/useGetRepaymentsPanelData';

type WidgetKey = 'overdue' | 'future' | 'past' | 'repayEarly';

const WIDGET_ORDER: WidgetKey[] = ['overdue', 'future', 'past', 'repayEarly'];

export const TabularRepaymentsPanel = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => (
    <Suspense fallback={<MainContentPending />}>
        <TabularRepaymentsWidgets accountId={accountId} />
    </Suspense>
);

const TabularRepaymentsWidgets = ({
    accountId
}: {
    accountId: NonNullable<TAccount['account_id']>;
}) => {
    const { isOverdue, isRBL } = useGetRepaymentsPanelData(accountId);

    const widgetConfig: Record<
        WidgetKey,
        { isVisible: boolean; element: React.ReactNode }
    > = {
        overdue: {
            isVisible: isOverdue,
            element: <OverduePayments accountId={accountId} />
        },
        future: {
            isVisible: !isRBL,
            element: <FutureRepayments accountId={accountId} />
        },
        past: {
            isVisible: true,
            element: <PastRepayments accountId={accountId} />
        },
        repayEarly: {
            isVisible: !isOverdue && !isRBL,
            element: <RepayEarlyForFree accountId={accountId} />
        }
    };

    return (
        <div
            data-testid="tabular-repayments-panel"
            className="col-span-5 flex flex-col gap-l max-sm:col-span-full"
        >
            {WIDGET_ORDER.map((key) => {
                const { isVisible, element } = widgetConfig[key];
                return isVisible ? (
                    <Fragment key={key}>{element}</Fragment>
                ) : null;
            })}
        </div>
    );
};
